import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "stores";
import store from "stores/interfaces";
import styled from "styled-components";
import theme from "theme";
import {
  Typography,
  Paper,
  IconButton,
  Box,
  useMediaQuery,
} from "@mui/material";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import AlarmIcon from "@mui/icons-material/Alarm";
import LoopIcon from "@mui/icons-material/Loop";
import PauseCircleFilledIcon from "@mui/icons-material/PauseCircleFilled";
import alerm from "assets/alerm.mp3";
import { createSelector } from "reselect";
import { registerListener, socket } from "modules/socket";
import { useDraggable } from "@dnd-kit/core";
import { AudioPlayer } from "modules/audioplayer";

const pad = (duration: number) => {
  return ("0" + duration).slice(-2);
};

const sound = new AudioPlayer({
  src: alerm,
  autoplay: false,
  loop: false,
  volume: 0.2,
  enableWebAudioGain: true,
  disableMesureMetric: true,
});

const stopPropagation = (e) => e.stopPropagation();

const selector = createSelector(
  [store.getCurrentRoomTimer, (state) => store.getAppState(state, "muted")],
  (timer, muted) => {
    return {
      timer,
      muted,
    };
  }
);

export type TimerRef = {
  start: (durationMs: number) => void;
  pause: () => void;
};

const Timer = forwardRef<TimerRef, { roomId: string }>(({ roomId }, ref) => {
  const d = useAppDispatch();

  // States
  const { timer, muted } = useAppSelector(selector);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [durationMs, setDurationMs] = useState<number>(timer.duration * 1000);
  const [elapsedMs, setElapsedMs] = useState<number>(
    (timer.duration - timer.current) * 1000
  );
  const playing = useRef<boolean>(false);

  useEffect(() => {
    setDurationMs(timer.duration * 1000);
  }, [timer.duration]);

  useEffect(() => {
    sound.mute(muted);
  }, [muted]);

  const now = Date.now();
  const currentTimeMs = startTime
    ? durationMs - elapsedMs - (now - startTime)
    : durationMs - elapsedMs;
  const currentTime = Math.ceil(currentTimeMs / 1000);
  const ended = currentTime <= 0;
  const paused = startTime == null;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    node: containerRef,
  } = useDraggable({
    id: "timer",
  });

  const [position, setPosition] = useState({
    x: 0,
    y: 0,
  });

  // Periodic works
  const [frame, tick] = useState(0);
  useEffect(() => {
    if (!paused && !ended) {
      playing.current = !ended;
      const timer = setTimeout(() => {
        tick((frame) => frame + 1);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [frame, paused, ended, playing]);

  useEffect(() => {
    if (ended && playing.current) {
      sound.play();
      playing.current = false;
    }
  }, [ended, playing]);

  // Operations
  const startTimerLocal = useCallback(
    (durationMs: number, elapsedMs: number) => {
      playing.current = false;
      setDurationMs(durationMs);
      setElapsedMs(elapsedMs);

      setStartTime(Date.now());
      playing.current = true;
    },
    [setDurationMs, setElapsedMs, setStartTime, playing]
  );

  const startTimerGlobal = useCallback(
    (durationMs: number, elapsedMs: number) => {
      startTimerLocal(durationMs, elapsedMs);

      socket.emit("startTimer", {
        roomId,
        durationMs,
        elapsedMs,
      });
    },
    [startTimerLocal, roomId]
  );

  const pauseTimerLocal = useCallback(
    (durationMs: number, elapsedMs: number) => {
      setDurationMs(durationMs);
      setElapsedMs(elapsedMs);

      setStartTime(null);
      playing.current = false;
    },
    [setDurationMs, setElapsedMs, setStartTime, playing]
  );

  // Click handlers
  const onStart = useCallback(() => {
    startTimerGlobal(durationMs, elapsedMs);
  }, [startTimerGlobal, durationMs, elapsedMs]);

  const onPause = useCallback(() => {
    const newElapsedMs = ended ? 0 : durationMs - currentTimeMs;

    pauseTimerLocal(durationMs, newElapsedMs);

    socket.emit("pauseTimer", {
      roomId,
      durationMs,
      elapsedMs: newElapsedMs,
    });

    d(
      store.updateCurrentRoom({
        timer: {
          duration: Math.floor(durationMs / 1000),
          startTime: 0,
          current: currentTime,
        },
      })
    );
  }, [
    d,
    pauseTimerLocal,
    durationMs,
    currentTimeMs,
    currentTime,
    ended,
    roomId,
  ]);

  const onReset = useCallback(() => {
    startTimerGlobal(durationMs, 0);
  }, [startTimerGlobal, durationMs]);

  const onOpen = useCallback(() => {
    d(
      store.appStateMutate((state) => {
        state.openRoomTimerSetting = true;
      })
    );
  }, [d]);

  // Forwarding
  useImperativeHandle(
    ref,
    () => ({
      start: (durationMs: number) => {
        startTimerGlobal(durationMs, 0);
      },
      pause: () => {
        onPause();
      },
    }),
    [startTimerGlobal, onPause]
  );

  // Socket listeners
  useEffect(() => {
    const lisner = ({
      durationMs,
      elapsedMs,
    }: {
      durationMs: number;
      elapsedMs: number;
    }) => {
      startTimerLocal(durationMs, elapsedMs);
    };
    return registerListener("startTimer", lisner);
  }, [startTimerLocal]);

  useEffect(() => {
    const lisner = ({
      durationMs,
      elapsedMs,
    }: {
      durationMs: number;
      elapsedMs: number;
    }) => {
      pauseTimerLocal(durationMs, elapsedMs);
    };
    return registerListener("pauseTimer", lisner);
  }, [pauseTimerLocal]);

  // Prepare display
  const sec = currentTime % 60;
  const min = Math.floor(currentTime / 60) % 60;
  const hour = Math.floor(currentTime / 60 / 60);

  const style = {
    transform: `translate3d(${position.x + (transform?.x || 0)}px, ${
      position.y + (transform?.y || 0)
    }px, 0)`,
  };

  const offsetRight = useOffsetRightByDrawer();
  const offsetTop = useOffsetTop();

  const handleDragEnd = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const x = position.x + (transform?.x || 0);
    const y = position.y + (transform?.y || 0);
    const rect = containerRef.current?.getBoundingClientRect();

    if (
      -x < 0 ||
      y < 0 ||
      window.innerWidth < -x + offsetRight + rect.width ||
      window.innerHeight < y + offsetTop + rect.height
    ) {
      return;
    }

    setPosition({
      x,
      y,
    });
  }, [position, transform, offsetRight, offsetTop, containerRef]);

  const restrictInWindow = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const { x, y } = position;
    const rect = containerRef.current?.getBoundingClientRect();

    if (
      window.innerWidth < -x + offsetRight + rect.width ||
      window.innerHeight < y + offsetTop + rect.height
    ) {
      setPosition({
        x: Math.max(x, -window.innerWidth + rect.width), // Drawerの開閉分は加味しない
        y: Math.min(y, window.innerHeight - offsetTop - rect.height),
      });
    }
  }, [position, containerRef, offsetRight, offsetTop, setPosition]);

  useEffect(() => {
    window.addEventListener("resize", restrictInWindow);
    return () => {
      window.removeEventListener("resize", restrictInWindow);
    };
  }, [restrictInWindow]);

  return (
    <TimerConteiner
      elevation={5}
      onDoubleClick={stopPropagation}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
      onPointerUp={handleDragEnd}
    >
      <Box pl={1} pr={1}>
        <Typography variant="h2" sx={{ fontFamily: "Roboto" }}>
          {ended ? "00:00:00" : `${pad(hour)}:${pad(min)}:${pad(sec)}`}
        </Typography>
      </Box>
      <Controls style={{ pointerEvents: "auto" }} onClick={stopPropagation}>
        <IconButton onClick={onOpen} size="large" data-no-dnd="true">
          <AlarmIcon />
        </IconButton>
        {paused ? (
          <IconButton onClick={onStart} size="large" data-no-dnd="true">
            <PlayCircleFilledIcon fontSize="large" />
          </IconButton>
        ) : (
          <IconButton onClick={onPause} size="large" data-no-dnd="true">
            <PauseCircleFilledIcon fontSize="large" />
          </IconButton>
        )}
        <IconButton
          onClick={onReset}
          disabled={!ended}
          size="large"
          data-no-dnd="true"
        >
          <LoopIcon />
        </IconButton>
      </Controls>
    </TimerConteiner>
  );
});

const useOffsetRightByDrawer = () => {
  const openRight = useAppSelector(
    (state) => !!store.getAppState(state, "openRoomDisplays") // todo
  );
  const openLeft = useAppSelector(
    (state) => !!store.getAppState(state, "openRoomControls")
  );
  const width = useAppSelector(store.getMenuWidth);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const pdfOpen = useAppSelector((state) => state.app.state.openPDFViewer);
  const pdfWidth = useAppSelector(
    (state) => state.app.state.openPDFViewerWidth
  );

  if (pdfOpen) {
    return parseInt(pdfWidth);
  }

  if ((openRight || openLeft) && !isMobile) {
    return parseInt(width);
  }

  return 0;
};

const useOffsetTop = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  return isMobile ? topOffsetSm : topOffset;
};

const topOffset = 64 + 38;
const topOffsetSm = 56 + 38;

const TimerConteiner = styled(Paper)`
  position: absolute;
  top: ${topOffset}px;
  right: 0;
  overflow: hidden;
  ${theme.breakpoints.down("sm")} {
    top: ${topOffsetSm}px;
  }
`;

const Controls = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-around;
  background: rgba(0, 0, 0, 0.74);
  transition: opacity 200ms linear;
  opacity: 0;
  &:hover {
    opacity: 1;
  }
`;

export default memo(Timer);
